import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut, ArrowLeft, RefreshCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  const token = typeof window !== "undefined" ? localStorage.getItem("admin_token") : null;
  const adminEmail = typeof window !== "undefined" ? localStorage.getItem("admin_email") : null;

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await axios.get(`${API}/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContacts(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
        return;
      }
      setErr("Erreur de chargement.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] py-12 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4 mr-2" /> Site public
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 hidden md:inline">{adminEmail}</span>
            <Button variant="outline" size="sm" onClick={load} data-testid="admin-refresh">
              <RefreshCcw className="w-4 h-4 mr-2" /> Actualiser
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout">
              <LogOut className="w-4 h-4 mr-2" /> Déconnexion
            </Button>
          </div>
        </div>

        <h1 className="font-display text-3xl text-slate-900 font-medium">Demandes de contact</h1>
        <p className="text-slate-600 mt-2">{contacts.length} demande(s) reçue(s)</p>

        <div className="mt-8 bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-500">Chargement…</div>
          ) : err ? (
            <div className="p-12 text-center text-red-700">{err}</div>
          ) : contacts.length === 0 ? (
            <div className="p-12 text-center text-slate-500">Aucune demande pour l'instant.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="contacts-table">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Nom</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Téléphone</th>
                    <th className="px-4 py-3">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {contacts.map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                        {new Date(c.created_at).toLocaleString("fr-CH")}
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {c.prenom} {c.nom}
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <a className="underline" href={`mailto:${c.email}`}>{c.email}</a>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <a className="underline" href={`tel:${c.telephone}`}>{c.telephone}</a>
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-md truncate">{c.message || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
